import jwt from 'jwt-decode';
import moment from 'moment';

import { getLastMessagePreview } from './internalAttachments';
import { uuidv4 } from './uuidGenerator';

/** Permissões de participante na Corporate Chat API. View é implícita: todo
 *  participante tem, e o front nunca a envia.
 */
export const GROUP_PERMISSIONS = {
    VIEW: 1,
    REPLY: 2,
    MANAGE: 3
};

/** Níveis de acesso mostrados na interface. São cumulativos: quem gerencia também
 *  envia mensagens.
 */
export const GROUP_ACCESS_LEVELS = {
    VIEW: 'view',
    REPLY: 'reply',
    MANAGE: 'manage'
};

export const DEFAULT_GROUP_ACCESS_LEVEL = GROUP_ACCESS_LEVELS.REPLY;

/** Os rótulos são ids do `react-intl`: a tradução fica na camada de componente. */
export const GROUP_ACCESS_LEVEL_LABELS = {
    [GROUP_ACCESS_LEVELS.VIEW]: 'Apenas visualizar',
    [GROUP_ACCESS_LEVELS.REPLY]: 'Enviar mensagens',
    [GROUP_ACCESS_LEVELS.MANAGE]: 'Gerenciar grupo'
};

/** Papel de cadastro, no campo numérico `role` de `users/agents/corporate/all`. */
export const USER_ROLES = {
    ADMINISTRATOR: 1,
    SUPERVISOR: 2,
    AGENT: 3
};

export const permissionsFromAccessLevel = (level) => {
    if (level === GROUP_ACCESS_LEVELS.MANAGE) return [GROUP_PERMISSIONS.REPLY, GROUP_PERMISSIONS.MANAGE];
    if (level === GROUP_ACCESS_LEVELS.REPLY) return [GROUP_PERMISSIONS.REPLY];
    return [];
}

export const accessLevelFromPermissions = (permissions) => {
    const list = permissions || [];
    if (list.includes(GROUP_PERMISSIONS.MANAGE)) return GROUP_ACCESS_LEVELS.MANAGE;
    if (list.includes(GROUP_PERMISSIONS.REPLY)) return GROUP_ACCESS_LEVELS.REPLY;
    return GROUP_ACCESS_LEVELS.VIEW;
}

/** A API recusa (400) Manage para agente. O teste é positivo: papel vazio ou
 *  desconhecido também fica sem a opção, porque é melhor escondê-la do que deixar
 *  o envio falhar.
 */
export const canReceiveManagePermission = (role) => {
    return role === USER_ROLES.ADMINISTRATOR || role === USER_ROLES.SUPERVISOR;
}

/** Quem já gerencia continua com a opção no seletor mesmo sem papel conhecido,
 *  senão o campo não teria como mostrar o valor atual.
 */
export const getAccessLevelOptions = (role, currentLevel) => {
    const levels = [GROUP_ACCESS_LEVELS.VIEW, GROUP_ACCESS_LEVELS.REPLY];
    if (canReceiveManagePermission(role) || currentLevel === GROUP_ACCESS_LEVELS.MANAGE) {
        levels.push(GROUP_ACCESS_LEVELS.MANAGE);
    }
    return levels;
}

/** O papel do usuário logado vem da claim `role` do token, que é o que a Corporate
 *  Chat API usa para autorizar as rotas de grupo.
 */
export const getTokenRole = (accessToken) => {
    if (!accessToken) return '';
    try {
        const { role } = jwt(accessToken);
        return role ? String(role).toLowerCase() : '';
    } catch (e) {
        return '';
    }
}

/** Criar grupo é restrito a administrador e supervisor: agente leva 403. */
export const canCreateInternalGroups = (tokenRole) => {
    return tokenRole === 'administrator' || tokenRole === 'supervisor';
}

export const buildGroupParticipants = (selectedUsers, participantLevels) => {
    return (selectedUsers || []).map(user => ({
        userId: user.id,
        permissions: permissionsFromAccessLevel((participantLevels || {})[user.id] || DEFAULT_GROUP_ACCESS_LEVEL)
    }));
}

/** O grupo pode nascer vazio (sem nenhuma mensagem). Só com anexo, vai uma mensagem
 *  sem texto, que é onde a API pendura os arquivos.
 */
export const resolveInitialGroupMessages = (messages, files) => {
    if (messages && messages.length > 0) {
        return messages.map(message => ({
            messageText: message.messageText,
            externalReference: message.externalReference
        }));
    }
    if (files && files.length > 0) {
        return [{ messageText: '', externalReference: uuidv4() }];
    }
    return [];
}

/** A listagem de conversas internas traz os grupos misturados às conversas
 *  individuais. No item de grupo, `id` é o id do grupo e `userId` vem 0: ele não
 *  pode virar uma conversa individual.
 */
export const splitConversationsAndGroups = (items) => {
    const list = items || [];
    return {
        conversations: list.filter(item => !item.isGroup),
        groups: list.filter(item => item.isGroup).map(item => ({
            id: item.id,
            name: item.name,
            lastMessage: item.lastMessage,
            dateLastMessage: item.dateLastMessage,
            totalUnread: item.totalUnread || 0,
            messages: [],
            messagesLoaded: false
        }))
    };
}

/** Sem duplicar por `id` e em ordem crescente de `id`, que cresce com o tempo. */
export const mergeGroupMessages = (currentMessages, incomingMessages) => {
    const byId = new Map();
    [...(currentMessages || []), ...(incomingMessages || [])].forEach(message => {
        if (message && message.id !== null && typeof message.id !== 'undefined') {
            byId.set(message.id, message);
        }
    });
    return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** Grupo que já está no estado (criado ou recebido pelo socket enquanto a listagem
 *  carregava) é mais completo do que o resumo da listagem, e não é sobrescrito.
 */
export const mergeListedGroups = (currentGroups, listedGroups) => {
    const knownIds = new Set((currentGroups || []).map(group => group.id));
    return [
        ...(currentGroups || []),
        ...(listedGroups || []).filter(group => !knownIds.has(group.id))
    ];
}

/** Grupo novo entra no topo. Grupo conhecido é atualizado pelo id, preservando o que
 *  só o front sabe: mensagens já carregadas, não lidas e se o histórico já foi buscado.
 *  As mensagens da criação somam às conhecidas, mas não contam como histórico
 *  carregado: ao abrir o grupo a primeira página é buscada mesmo assim.
 */
export const upsertGroup = (groups, group) => {
    const list = groups || [];
    const { messages, ...groupData } = group;
    const existingGroup = list.find(item => item.id === group.id);

    if (!existingGroup) {
        return [{
            ...groupData,
            messages: mergeGroupMessages([], messages),
            totalUnread: groupData.totalUnread || 0,
            messagesLoaded: false
        }, ...list];
    }

    return list.map(item => {
        if (item.id !== group.id) return item;
        return {
            ...item,
            ...groupData,
            participants: groupData.participants || item.participants,
            messages: mergeGroupMessages(item.messages, messages),
            totalUnread: item.totalUnread,
            messagesLoaded: item.messagesLoaded
        };
    });
}

export const GROUP_EXIT_REASONS = {
    DELETED: 'deleted',
    REMOVED: 'removed'
};

/** Motivo para o grupo sair da lista do usuário logado. Sem lista de participantes,
 *  ou sem saber quem está logado, não há como afirmar que ele saiu.
 */
export const getGroupExitReason = (group, loggedUserId) => {
    if (!group) return null;
    if (group.isActive === false) return GROUP_EXIT_REASONS.DELETED;

    const participants = group.participants;
    if (loggedUserId && Array.isArray(participants) && participants.length > 0 &&
        !participants.some(participant => participant.userId === loggedUserId)) {
        return GROUP_EXIT_REASONS.REMOVED;
    }

    return null;
}

export const removeGroup = (groups, groupId) => {
    return (groups || []).filter(group => group.id !== groupId);
}

/** Resposta de gestão, evento de atualização ou busca ao abrir: grupo excluído, ou do
 *  qual o usuário logado não participa mais, sai da lista; senão é atualizado pelo id.
 */
export const applyGroupUpdate = (groups, group, loggedUserId) => {
    if (getGroupExitReason(group, loggedUserId)) return removeGroup(groups, group.id);
    return upsertGroup(groups, group);
}

export const patchGroup = (groups, groupId, changes) => {
    return (groups || []).map(group => group.id === groupId ? { ...group, ...changes } : group);
}

export const addGroupMessages = (groups, groupId, messages, changes = {}) => {
    return (groups || []).map(group => {
        if (group.id !== groupId) return group;
        return {
            ...group,
            ...changes,
            messages: mergeGroupMessages(group.messages, messages)
        };
    });
}

/** A resposta do envio já é a mensagem gravada (data do servidor e anexos com `url`).
 *  A documentação diz lista e o swagger diz objeto: aceita os dois. Sem mensagem, o
 *  envio é tratado como falha.
 */
export const resolveSentGroupMessage = (data) => {
    const message = Array.isArray(data) ? data[0] : data;
    return message && typeof message === 'object' && message.id !== null && typeof message.id !== 'undefined'
        ? message
        : null;
}

/** Mensagem nova recebida pelo socket. O evento não traz o nome do grupo, então grupo
 *  desconhecido é ignorado; mensagem com id já conhecido também (não conta de novo).
 *  Com a janela do grupo aberta as não lidas voltam a 0.
 */
export const receiveGroupMessage = (groups, message, openGroupId) => {
    const list = groups || [];
    const group = message ? list.find(item => item.id === message.groupId) : null;

    if (!group) return list;
    if ((group.messages || []).some(item => item.id === message.id)) return list;

    return list.map(item => {
        if (item.id !== group.id) return item;
        return {
            ...item,
            messages: mergeGroupMessages(item.messages, [message]),
            totalUnread: openGroupId === item.id ? 0 : (item.totalUnread || 0) + 1
        };
    });
}

export const getParticipantAccessLevel = (group, userId) => {
    const participant = ((group && group.participants) || []).find(item => item.userId === userId);
    return participant ? accessLevelFromPermissions(participant.permissions) : null;
}

/** Quem pode gerenciar o grupo:
 *  - grupo excluído não é gerenciado;
 *  - agente (pelo token) nunca gerencia, mesmo sem a lista de participantes;
 *  - administrador ou supervisor com a lista desconhecida: a gestão aparece e a API decide;
 *  - com a lista conhecida, só quem tem o nível "Gerenciar grupo".
 */
export const canManageGroup = (group, tokenRole, loggedUserId) => {
    if (!group || group.isActive === false) return false;
    if (!canCreateInternalGroups(tokenRole)) return false;
    if (!Array.isArray(group.participants)) return true;
    return getParticipantAccessLevel(group, loggedUserId) === GROUP_ACCESS_LEVELS.MANAGE;
}

/** O grupo não pode ficar sem gestor: o único participante com "Gerenciar grupo" não
 *  pode sair, ser removido nem ter o nível trocado. A API recusa a remoção (400), e o
 *  rebaixamento teria o mesmo efeito.
 */
export const isLastGroupManager = (group, userId) => {
    const managers = ((group && group.participants) || [])
        .filter(participant => accessLevelFromPermissions(participant.permissions) === GROUP_ACCESS_LEVELS.MANAGE);
    return managers.length === 1 && managers[0].userId === userId;
}

export const GROUP_SEND_BLOCKS = {
    DELETED: 'deleted',
    VIEW_ONLY: 'viewOnly'
};

/** Com a lista de participantes desconhecida o envio não é bloqueado: a API decide
 *  (e devolve 400 com mensagem se o usuário não puder).
 */
export const getGroupSendBlock = (group, loggedUserId) => {
    if (!group) return null;
    if (group.isActive === false) return GROUP_SEND_BLOCKS.DELETED;

    if (Array.isArray(group.participants)) {
        const participant = group.participants.find(item => item.userId === loggedUserId);
        if (!participant || accessLevelFromPermissions(participant.permissions) === GROUP_ACCESS_LEVELS.VIEW) {
            return GROUP_SEND_BLOCKS.VIEW_ONLY;
        }
    }

    return null;
}

/** O nome de quem enviou aparece só nas mensagens de outra pessoa, e só no começo
 *  de uma sequência: quando a anterior é de outro remetente, de outro dia, ou não
 *  existe. O dia é comparado como no divisor de datas da conversa.
 */
export const shouldShowGroupSenderName = (message, prevMessage, loggedUserId) => {
    if (!message || message.userFromId === loggedUserId) return false;
    if (!prevMessage) return true;
    if (prevMessage.userFromId !== message.userFromId) return true;
    return !moment(message.createdAt).isSame(prevMessage.createdAt, 'day');
}

/** A ordem de `messages` nas respostas não é documentada: a última é a de maior id. */
export const getGroupLastMessage = (group) => {
    const messages = (group && group.messages) || [];
    return messages.reduce((last, message) => (!last || message.id > last.id ? message : last), null);
}

export const getGroupActivityDate = (group) => {
    const lastMessage = getGroupLastMessage(group);
    return (lastMessage && lastMessage.createdAt) || (group && group.dateLastMessage) || (group && group.createdAt) || null;
}

/** Devolve o texto da prévia ou, na falta dele, a quantidade de participantes (que
 *  precisa do `react-intl` para virar texto). `null` quando não há o que mostrar.
 */
export const getGroupPreview = (group) => {
    const lastMessage = getGroupLastMessage(group);
    const lastMessagePreview = lastMessage ? getLastMessagePreview(lastMessage.messageText, lastMessage.attachments) : '';
    if (lastMessagePreview) return { text: lastMessagePreview };
    if (group && group.lastMessage) return { text: group.lastMessage };
    if (group && Array.isArray(group.participants)) return { participantsCount: group.participants.length };
    return null;
}

/** 400 de regra de negócio vem como `{ data: "mensagem legível" }`; 400 de validação
 *  vem no formato ProblemDetails, com as mensagens em `errors`. Sem mensagem da API,
 *  quem chama mostra o texto genérico da ação.
 */
export const getApiErrorMessage = (error) => {
    const body = error && error.response && error.response.data;
    if (!body) return null;

    if (typeof body.data === 'string' && body.data.trim()) return body.data;

    if (body.errors && typeof body.errors === 'object') {
        const messages = Object.values(body.errors).reduce((acc, value) => acc.concat(value), []);
        const firstMessage = messages.find(message => typeof message === 'string' && message.trim());
        if (firstMessage) return firstMessage;
    }

    return null;
}
